import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getUniqueMentionedUsernames } from '@/utils/mentions';

export interface MentionUser {
  id: string;
  username: string;
  name: string | null;
  profile_image_url: string | null;
}

/**
 * Hook to search users for @mention autocomplete
 */
export function useUserSearch(query: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['user-search', query],
    queryFn: async () => {
      if (!query || query.length < 1) return [];
      
      const { data, error } = await supabase
        .from('public_profiles')
        .select('id, username, name, profile_image_url')
        .or(`username.ilike.%${query}%,name.ilike.%${query}%`)
        .limit(10)
        .order('username');
      
      if (error) {
        console.error('Error searching users:', error);
        return [];
      }
      
      return data as MentionUser[];
    },
    enabled: enabled && query.length > 0,
    staleTime: 30000, // Cache for 30 seconds
  });
}

/**
 * Hook to verify if usernames exist in the database
 */
export function useVerifyUsernames(usernames: string[]) {
  return useQuery({
    queryKey: ['verify-usernames', usernames.join(',')],
    queryFn: async () => {
      if (usernames.length === 0) return {};
      
      const { data, error } = await supabase
        .from('public_profiles')
        .select('id, username')
        .in('username', usernames);
      
      if (error) {
        console.error('Error verifying usernames:', error);
        return {};
      }
      
      // Create a map of username -> user_id
      const usernameMap: Record<string, string> = {};
      data?.forEach(user => {
        usernameMap[user.username.toLowerCase()] = user.id;
      });
      
      return usernameMap;
    },
    enabled: usernames.length > 0,
    staleTime: 60000, // Cache for 1 minute
  });
}

/**
 * Hook to create mentions in the database
 */
export function useCreateMentions() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      contentType,
      contentId,
      text,
      mentionerUserId
    }: {
      contentType: 'bulletin' | 'loop' | 'comment';
      contentId: string;
      text: string;
      mentionerUserId: string;
    }) => {
      // Extract unique usernames from text
      const usernames = getUniqueMentionedUsernames(text);
      
      if (usernames.length === 0) {
        return { success: true, mentionCount: 0 };
      }
      
      // Get user IDs for mentioned usernames
      const { data: users, error: userError } = await supabase
        .from('public_profiles')
        .select('id, username')
        .in('username', usernames);
      
      if (userError) {
        console.error('Error fetching mentioned users:', userError);
        throw userError;
      }
      
      if (!users || users.length === 0) {
        return { success: true, mentionCount: 0 };
      }
      
      // Create mention records
      const mentions = users.map(user => ({
        content_type: contentType,
        content_id: contentId,
        mentioned_user_id: user.id,
        mentioner_user_id: mentionerUserId
      }));
      
      const { error: insertError } = await supabase
        .from('mentions')
        .insert(mentions);
      
      if (insertError) {
        console.error('Error creating mentions:', insertError);
        throw insertError;
      }
      
      return { success: true, mentionCount: mentions.length };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mentions'] });
    }
  });
}

/**
 * Hook to get mentions for a specific piece of content
 */
export function useContentMentions(contentType: string, contentId: string) {
  return useQuery({
    queryKey: ['mentions', contentType, contentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mentions')
        .select(`
          id,
          mentioned_user_id,
          mentioner_user_id,
          created_at,
          profiles!mentioned_user_id(
            username,
            name,
            profile_image_url
          )
        `)
        .eq('content_type', contentType)
        .eq('content_id', contentId);
      
      if (error) {
        console.error('Error fetching mentions:', error);
        return [];
      }
      
      return data;
    },
    enabled: !!contentId && !!contentType,
  });
}

/**
 * Hook to delete mentions when content is deleted
 */
export function useDeleteMentions() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      contentType,
      contentId
    }: {
      contentType: 'bulletin' | 'loop' | 'comment';
      contentId: string;
    }) => {
      const { error } = await supabase
        .from('mentions')
        .delete()
        .eq('content_type', contentType)
        .eq('content_id', contentId);
      
      if (error) {
        console.error('Error deleting mentions:', error);
        throw error;
      }
      
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mentions'] });
    }
  });
}
